import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, AlertTriangle, Copy, Check } from "lucide-react";
import { API, formatApiErrorDetail } from "../auth/api";
import { useAuth } from "../auth/AuthContext";

// Used as the forced enrolment screen for admins (forced=true) and as the
// opt-in panel members open from their account menu.
export default function MfaSetup({ forced = false, onDone, onCancel }) {
  const { user, logout, refresh } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const start = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/auth/mfa/enroll");
      setSetup(data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    start();
  }, [start]);

  const activate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await API.post("/auth/mfa/activate", { code });
      setCodes(data.recovery_codes);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText((codes || []).join("\n"));
    setCopied(true);
  };

  const finish = async () => {
    await refresh();
    if (onDone) onDone();
    else window.location.reload();
  };

  return (
    <div
      className={
        forced
          ? "min-h-screen w-screen flex items-center justify-center bg-[#09090B] text-zinc-100 grid-bg p-6"
          : "fixed inset-0 z-[2100] flex items-start justify-center bg-black/80 backdrop-blur-sm p-6 overflow-auto"
      }
      data-testid="mfa-setup-root"
    >
      <div className="w-full max-w-md my-8">
        <div className="border border-white/10 bg-[#0a0a0c]">
          <div className="px-6 pt-6 pb-5 border-b border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-emerald-300">
                {forced ? "Action Required" : "Account Security"}
              </span>
            </div>
            <h1 className="font-display font-black text-3xl leading-[1] tracking-tight text-zinc-50 mb-2">
              TWO-STEP VERIFICATION
            </h1>
            <p className="text-[13px] leading-relaxed text-zinc-400">
              {codes
                ? "Save these recovery codes now. Each one works once and they are not shown again."
                : "Scan this code with an authenticator app (Google Authenticator, 1Password, Authy), then enter the 6-digit code it shows."}
            </p>
            {user?.email && (
              <p className="mt-2 font-mono text-[10px] tracking-[0.16em] text-zinc-600 uppercase">
                {user.email}
              </p>
            )}
          </div>

          {codes ? (
            <div className="p-6 space-y-4" data-testid="mfa-recovery-codes">
              <div className="grid grid-cols-2 gap-2">
                {codes.map((c) => (
                  <div
                    key={c}
                    className="border border-white/10 bg-[#09090B] px-2 py-2 font-mono text-[12px] text-emerald-200 text-center"
                  >
                    {c}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={copyCodes}
                data-testid="mfa-copy-codes"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-white/10 hover:border-cyan-400/60 font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-300 hover:text-cyan-300 transition-colors"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied" : "Copy Codes"}
              </button>
              <p className="font-mono text-[10px] leading-relaxed text-amber-300/80">
                If you lose your phone and these codes, only whoever runs the backend can reset
                your two-step verification.
              </p>
              <button
                type="button"
                onClick={finish}
                data-testid="mfa-setup-done"
                className="w-full px-4 py-2.5 border border-emerald-400/60 bg-emerald-400/10 hover:bg-emerald-400/20 font-mono text-[11px] tracking-[0.22em] uppercase text-emerald-200 transition-colors"
              >
                I've Saved Them →
              </button>
            </div>
          ) : (
            <form onSubmit={activate} className="p-6 space-y-4">
              {setup?.qr_svg && (
                <div className="flex justify-center">
                  <img
                    src={setup.qr_svg}
                    alt="Authenticator QR code"
                    data-testid="mfa-qr"
                    className="w-44 h-44 bg-white p-2"
                  />
                </div>
              )}
              {setup?.manual_key && (
                <div className="text-center">
                  <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-600 mb-1">
                    Or enter this key manually
                  </div>
                  <div
                    className="font-mono text-[11px] text-zinc-300 break-all"
                    data-testid="mfa-manual-key"
                  >
                    {setup.manual_key}
                  </div>
                </div>
              )}

              <div>
                <label
                  htmlFor="mfa-activate-code"
                  className="block font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5"
                >
                  6-Digit Code
                </label>
                <input
                  id="mfa-activate-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  required
                  data-testid="mfa-activate-code"
                  className="w-full bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none px-3 py-2.5 font-mono text-[16px] tracking-[0.4em] text-center text-zinc-100 placeholder:text-zinc-700 transition-colors"
                />
              </div>

              {error && (
                <div
                  className="flex items-start gap-2 border border-rose-400/30 bg-rose-400/5 px-3 py-2"
                  data-testid="mfa-setup-error"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
                  <span className="font-mono text-[11px] text-rose-200 leading-relaxed">
                    {error}
                  </span>
                </div>
              )}

              <button
                type="submit"
                disabled={busy || code.length !== 6}
                data-testid="mfa-activate-submit"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-emerald-400/60 bg-emerald-400/10 hover:bg-emerald-400/20 disabled:opacity-50 disabled:cursor-not-allowed font-mono text-[11px] tracking-[0.22em] uppercase text-emerald-200 transition-colors"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Working…
                  </>
                ) : (
                  <>Turn On →</>
                )}
              </button>

              <button
                type="button"
                onClick={() => (forced ? logout() : onCancel && onCancel())}
                data-testid="mfa-setup-cancel"
                className="w-full px-4 py-2 border border-white/10 hover:border-white/25 font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                {forced ? "Sign out instead" : "Cancel"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
